/**
 * AccountSelect — Headless UI Listbox for payment account selection.
 * Replaces native <select> so options can render badges (Personal / Shared)
 * and the trigger can show two lines (name + type · masked digits).
 */

import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ACCOUNT_TYPE_LABELS } from '../utils/paymentAccountHelpers';

/**
 * Renders a payment account row with name, type, masked digits, and ownership badge.
 * Shared between the selected-state ListboxButton and each ListboxOption.
 */
const renderAccountRow = (account, currentUserId) => {
  if (!account) return null;
  const name = account.nickName || account.nameOnAccount || 'Unnamed Account';
  const type = ACCOUNT_TYPE_LABELS[account.accountType] || account.accountType || '';
  const masked = account.maskedAccountNumber || '';
  const isOwnAccount = currentUserId && account.portalUserId === currentUserId;
  const isAgencyAccount = !account.portalUserId;

  const badge = isOwnAccount ? (
    <span
      title="Only visible to you"
      className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 flex-shrink-0"
    >
      Personal
    </span>
  ) : isAgencyAccount ? (
    <span
      title="Visible to all authorized users in this agency"
      className="inline-flex px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 flex-shrink-0"
    >
      Shared
    </span>
  ) : null;

  return (
    <div className="min-w-0 w-full">
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className="font-medium text-sm text-gray-900 dark:text-white truncate">{name}</span>
        {badge}
      </div>
      {(type || masked) && (
        <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
          {type}
          {type && masked && <span className="mx-1">·</span>}
          {masked}
        </div>
      )}
    </div>
  );
};

const AccountSelect = ({ value, onChange, accounts, currentUserId, ariaLabel }) => {
  const selected = accounts.find((a) => String(a.accountId) === String(value));
  return (
    <Listbox value={String(value || '')} onChange={onChange}>
      <div className="relative">
        <ListboxButton
          aria-label={ariaLabel}
          className="w-full px-4 py-3 pr-10 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-left focus:ring-2 focus:ring-primary/20 focus:border-primary"
        >
          {selected ? renderAccountRow(selected, currentUserId) : (
            <span className="text-sm text-gray-500 dark:text-gray-400">Select an account</span>
          )}
        </ListboxButton>
        <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 pointer-events-none" />
        {/* Render in a portal anchored to the trigger so options escape the modal's
            overflow clipping. `--button-width` is exposed by Headless UI when `anchor`
            is set, so we can size the panel to match the trigger. */}
        <ListboxOptions
          anchor="bottom start"
          className="z-[60] [width:var(--button-width)] [--anchor-gap:0.25rem] max-h-60 overflow-auto rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg focus:outline-none"
        >
          {accounts.map((account) => (
            <ListboxOption
              key={account.accountId}
              value={String(account.accountId)}
              className="cursor-pointer px-4 py-2 data-[focus]:bg-primary/5 dark:data-[focus]:bg-primary/10 data-[selected]:bg-primary/10 dark:data-[selected]:bg-primary/20"
            >
              {renderAccountRow(account, currentUserId)}
            </ListboxOption>
          ))}
        </ListboxOptions>
      </div>
    </Listbox>
  );
};

export default AccountSelect;
